import styled, { css, keyframes } from 'styled-components'
import { Drawer, Icon, Modal } from 'antd'
import { QuestionSubLabel, SelectInputStyled } from '@edulastic/common'
import {
  cream100,
  white,
  mobileWidthMax,
  smallDesktopWidth,
} from '@edulastic/colors'
import { BetaTag2 } from '../../../common/components/BetaTag'
import { StyledSelect } from '../common/styled'

const PENDING_MODAL_GREEN = '#158463'

const keyboardFocusVisibleStyles = css`
  &:focus-visible {
    outline: 2px solid ${PENDING_MODAL_GREEN};
    outline-offset: 2px;
  }
`

export const StyledGenerateStandardsBannerWrapper = styled.div`
  position: relative;
  padding-top: 12px;
  overflow: visible;
`

export const StyledGenerateStandardsBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  padding: 16px;
  background: #f1fcc7;
  border: 2px solid #151d34;
  border-radius: 4px;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'default')};
`

export const StyledBannerBetaTag = styled(BetaTag2)`
  position: absolute;
  top: 0;
  right: 6px;
  margin: 0;
  z-index: 1;
  border-radius: 5px;
`

export const StyledBannerRow = styled.div`
  display: flex;
  align-items: start;
  gap: 8px;
  width: 100%;

  svg {
    flex-shrink: 0;
  }
`

export const StyledBannerTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const StyledBannerText = styled.span`
  font-family: 'Open Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #000000;
`

export const StyledBannerLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  color: #000000;
  width: fit-content;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
  }
`

export const StyledStandardsDrawer = styled(Drawer)`
  .ant-drawer-content {
    .ant-drawer-body {
      padding: 0px;
      display: flex;
      flex-direction: column;
      height: 100%;
    }
  }
`

export const StyledDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: #f1fcc7;
  border-bottom: 2px solid #151d34;

  .drawer-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .drawer-title {
    font-family: 'Open Sans', sans-serif;
    font-weight: 600;
    font-size: 18px;
    line-height: 28px;
    color: #000000;
  }

  .drawer-close {
    display: inline-flex;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
  }
`

export const StyledDrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 24px 24px;

  .drawer-description {
    margin: 0;
    font-family: 'Open Sans', sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 22px;
    color: #555555;
  }

  .drawer-divider {
    width: 100%;
    height: 0;
    border-top: 1px solid #e6e6e6;
  }
`

export const StyledFieldsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

export const StyledDrawerInfoContainer = styled.div`
  width: 100%;
  padding: 8px;
  border-radius: 6px;
  background: ${cream100};
  border: 1px solid #a97c00;
`

export const StyledDrawerInfoList = styled.ul`
  margin: 0;
  padding-left: 21px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  list-style-type: disc;

  li {
    margin: 0;
  }
`

export const StyledStandardsSelect = styled(SelectInputStyled)`
  width: 100%;
  &&& .ant-select-selection {
    height: 40px;
    min-height: 40px;
    border: 1px solid #151d34;
    border-radius: 4px;
    box-shadow: none;
  }
  &&& .ant-select-selection__rendered {
    line-height: 38px;
    margin-left: 8px;
    font-family: 'Open Sans', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #151d34;
  }
  &&& .ant-select-arrow {
    color: #151d34;
  }
`

export const StyledGenerateButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 4px;
  background: #158463;
  border: 1px solid #158463;
  color: ${white};
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const dotBlink = keyframes`
  0% { opacity: 0.2; }
  20% { opacity: 1; }
  100% { opacity: 0.2; }
`

export const StyledItemStandardsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: 34px;
  margin: 20px 16px 10px 0px;
  padding: 0px 15px 0px 10px;
  background: #fafafa;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #000000;
  overflow: hidden;

  &.error {
    color: #c0392b;
  }

  svg {
    flex-shrink: 0;
  }
`

export const StyledItemStandardsContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`

export const StyledItemStandardsDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`

export const StyledItemStandardsTextWrap = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;

  > span {
    display: block;
    min-width: 0;
    overflow: hidden;
  }
`

export const StyledLoadingDot = styled.span`
  animation: ${dotBlink} 1.4s infinite both;
  animation-delay: ${(props) => props.delay || '0s'};
`

export const StyledStandardLabel = styled.span`
  white-space: nowrap;
`

export const StyledStandardText = styled.span`
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #000000;
  cursor: pointer;
`

export const StyledStandardMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;

  &::before {
    content: '';
    flex-shrink: 0;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #505e6f;
  }
`

export const StyledRowActions = styled.span`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 16px;
`

export const StyledSaveAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-weight: 700;
  color: #158463;
  text-transform: uppercase;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  ${keyboardFocusVisibleStyles}
`

export const StyledEditAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-weight: 700;
  color: #158463;
  text-transform: uppercase;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  ${keyboardFocusVisibleStyles}
`

export const StyledDiscardAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: none;
  border: none;
  padding: 6px;
  min-width: 24px;
  min-height: 24px;
  flex-shrink: 0;
  ${keyboardFocusVisibleStyles}

  svg {
    pointer-events: none;
  }
`

export const StyledPendingMetadataModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 6px;
  }
  .ant-modal-body {
    padding: 24px 0;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
`

export const StyledPendingMetadataModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
`

export const StyledPendingMetadataModalTitle = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
  color: #000000;
`

export const StyledPendingMetadataModalClose = styled(Icon)`
  font-size: 20px;
  color: #000000;
  cursor: pointer;
`

export const StyledPendingMetadataModalDivider = styled.div`
  height: 1px;
  width: 100%;
  background: #e0e0e0;
`

export const StyledPendingMetadataModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 24px;
`

export const StyledPendingMetadataModalBodyText = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #000000;
`

export const StyledPendingMetadataModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px;
`

const PendingModalButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const StyledPendingMetadataModalRejectButton = styled(
  PendingModalButton
)`
  background: #ffffff;
  border: 1px solid ${PENDING_MODAL_GREEN};
  color: ${PENDING_MODAL_GREEN};
`

export const StyledPendingMetadataModalAcceptButton = styled(
  PendingModalButton
)`
  min-width: 150px;
  background: ${PENDING_MODAL_GREEN};
  border: 1px solid ${PENDING_MODAL_GREEN};
  color: #ffffff;
`

export const StyledMetadataEditorWithLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
  margin-top: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
  ${({ flexProps }) => flexProps || ''};
`

export const StyledMetadataQuestionSubLabel = styled(QuestionSubLabel)`
  width: auto;
  flex-shrink: 0;
  margin-right: 5px;
  padding-left: 0;
`

export const StyledMetadataEditorRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
  height: auto;
  min-height: 60px;
  padding: 8px 16px;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-family: 'Open Sans', sans-serif;
  overflow: hidden;

  @media (max-width: ${smallDesktopWidth}) {
    flex-wrap: wrap;
    height: auto;
    min-height: 60px;
    row-gap: 8px;
    overflow: visible;
  }

  @media (max-width: ${mobileWidthMax}) {
    flex-wrap: wrap;
    height: auto;
    min-height: 60px;
    row-gap: 12px;
    align-items: flex-start;
    padding: 8px 12px;
    overflow: visible;
  }
`

export const StyledMetadataEditorRowWithLabel = styled(StyledMetadataEditorRow)`
  flex: 1;
  min-width: 0;
`

export const StyledMetadataEditorFields = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 44px;
  flex: 1 1 calc(100% - 72px);
  min-width: 0;
  overflow: hidden;

  @media (max-width: ${smallDesktopWidth}) {
    gap: 24px;
    flex: 1 1 calc(100% - 72px);
    flex-wrap: wrap;
    row-gap: 8px;
  }

  @media (max-width: ${mobileWidthMax}) {
    flex: 1 1 calc(100% - 72px);
    gap: 16px 20px;
    flex-wrap: wrap;
    overflow: visible;
  }
`

export const StyledMetadataStandardsField = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 375px;

  svg {
    flex-shrink: 0;
  }

  @media (max-width: ${mobileWidthMax}) {
    flex: 1 1 100%;
    max-width: 100%;
  }
`

export const StyledMetadataStandardsContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
`

export const StyledMetadataStandardsValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
  max-width: 100%;
`

export const StyledMetadataStandardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  width: 100%;
`

export const StyledMetadataStandardRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  width: 100%;
`

export const StyledMetadataStandardValueText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: block;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #000000;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

export const StyledStandardValueTooltipWrap = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;

  > span {
    display: block;
    min-width: 0;
    overflow: hidden;
  }
`

export const StyledMetadataDropdownField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  min-width: 0;
`

export const StyledMetadataDifficultyField = styled(
  StyledMetadataDropdownField
)`
  flex: 0 1 auto;
  min-width: 60px;
`

export const StyledMetadataDokField = styled(StyledMetadataDropdownField)`
  flex: 0 1 auto;
  min-width: 72px;
`

/** CON-756: Rubric VIEW control in bulk metadata panel (Figma 4296:1448). */
export const StyledMetadataRubricField = styled(StyledMetadataDropdownField)`
  flex: 0 0 auto;
  min-width: 72px;
`

export const StyledRubricActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  color: #158463;
  text-transform: uppercase;
  ${keyboardFocusVisibleStyles}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

export const StyledMetadataEditorActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 16px;

  @media (max-width: ${mobileWidthMax}) {
    padding-left: 8px;
    align-self: center;
  }
`

export const StyledMetadataFieldLabel = styled.label`
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #505e6f;
  white-space: nowrap;
`

export const StyledStandardValueText = styled.span`
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #000000;
`

export const StyledNoStandardsText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #8a8a8a;
`

export const StyledStandardsEditButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  ${keyboardFocusVisibleStyles}
`

export const StyledMetadataEditorSelect = styled(StyledSelect)`
  &&& {
    width: 100%;
    min-width: 0;
  }
  &&& > .ant-select-selection {
    width: auto;
    height: 18px;
    padding-right: 18px;
  }
  &&& > .ant-select-selection--single {
    height: 18px;
  }
  &&& > .ant-select-selection > .ant-select-selection__rendered {
    margin: 0;
    line-height: 18px;
    font-weight: 500;
    font-size: 12px;
    color: #000000;
  }
  &&& > .ant-select-selection .ant-select-selection-selected-value {
    position: relative;
    float: none;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &&& > .ant-select-selection .ant-select-arrow {
    right: 0;
    color: #505e6f;
  }
`

export const StyledMetadataEditorSaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-weight: 700;
  font-size: 12px;
  line-height: 18px;
  text-transform: uppercase;
  color: ${PENDING_MODAL_GREEN};
  ${keyboardFocusVisibleStyles}
`

export const StyledMetadataEditorClose = styled.button`
  display: inline-flex;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  ${keyboardFocusVisibleStyles}
`
